import { ChevronLeft, ChevronRight } from "lucide-mui"
import { DateTime } from "luxon"
import React from "react"

import i18n from "@/i18n"
import { type OpeningHour } from "@/utils/opening_hours"

import { isSidebarDateClosed } from "../reservation_sidebar_availability"

type MobileCalendarProps = {
  value: DateTime | null
  timezone: string
  country: string
  openingHours?: OpeningHour[]
  onChange: (value: DateTime) => void
}

// 2026-03-01 is a Sunday; anchors the fixed Sunday-first weekday header.
const SUNDAY_ANCHOR_ISO = "2026-03-01"

const formatWeekdayLabel = (date: DateTime) => {
  const shortLabel = date.toFormat("ccc").replace(".", "")
  return shortLabel.slice(0, 2).replace(/^./, (char) => char.toUpperCase())
}

const MobileCalendar: React.FC<MobileCalendarProps> = ({ value, timezone, country, openingHours, onChange }) => {
  const language = i18n.locale || "en"
  const locale = country ? `${language}-${country}` : language
  const today = DateTime.now().setZone(timezone).setLocale(locale).startOf("day")
  const selectedDate = value?.setZone(timezone).setLocale(locale).startOf("day") || null
  const selectedDateIso = selectedDate?.toISODate()
  const [visibleMonth, setVisibleMonth] = React.useState(() => (selectedDate || today).startOf("month"))

  React.useEffect(() => {
    if (!selectedDate) return
    setVisibleMonth((currentMonth) =>
      selectedDate.hasSame(currentMonth, "month") ? currentMonth : selectedDate.startOf("month"),
    )
  }, [selectedDateIso])

  const monthStart = visibleMonth.startOf("month")
  const firstDayIndex = monthStart.weekday % 7
  const daysInMonth = visibleMonth.daysInMonth ?? 30
  const totalCellCount = Math.ceil((firstDayIndex + daysInMonth) / 7) * 7
  const weekdayAnchor = DateTime.fromISO(SUNDAY_ANCHOR_ISO).setLocale(locale)

  const weekdayLabels = Array.from({ length: 7 }, (_, index) => formatWeekdayLabel(weekdayAnchor.plus({ days: index })))
  const monthDays = Array.from({ length: totalCellCount }, (_, index) => {
    const dayNumber = index - firstDayIndex + 1

    if (dayNumber < 1 || dayNumber > daysInMonth) return null

    return monthStart.set({ day: dayNumber }).startOf("day")
  })

  return (
    <div className="space-y-4" data-testid="public-venue-mobile-booking-calendar">
      <div className="flex items-center justify-between text-[#374151]">
        <button
          type="button"
          onClick={() => setVisibleMonth((currentMonth) => currentMonth.minus({ months: 1 }).startOf("month"))}
          className="flex h-[32px] w-[32px] items-center justify-center rounded-[6px] text-[#374151] transition-colors hover:bg-[#f4f6f8]"
          aria-label={i18n.t("app.public.venue.sidebar.previous_month")}
          data-testid="public-venue-mobile-booking-month-prev"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <div
          className="text-sm font-semibold capitalize leading-5 text-[#374151]"
          data-testid="public-venue-mobile-booking-month-label"
        >
          {visibleMonth.setLocale(locale).toFormat("LLLL yyyy")}
        </div>

        <button
          type="button"
          onClick={() => setVisibleMonth((currentMonth) => currentMonth.plus({ months: 1 }).startOf("month"))}
          className="flex h-[32px] w-[32px] items-center justify-center rounded-[6px] text-[#374151] transition-colors hover:bg-[#f4f6f8]"
          aria-label={i18n.t("app.public.venue.sidebar.next_month")}
          data-testid="public-venue-mobile-booking-month-next"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-x-[6px] gap-y-[6px]">
        {weekdayLabels.map((label, index) => (
          <div
            key={`${label}-${index}`}
            className="flex h-[22px] items-center justify-center text-[12px] leading-normal text-[#171717]"
          >
            {label}
          </div>
        ))}

        {monthDays.map((day, index) => {
          if (!day) {
            return <div key={`empty-${index}`} className="h-[34px]" aria-hidden="true" />
          }

          const isoDate = day.toISODate() || `${visibleMonth.year}-${visibleMonth.month}-${day.day}`
          const isPast = day < today
          const isClosed = isSidebarDateClosed(day, openingHours, timezone)
          const isToday = day.hasSame(today, "day")
          const isSelected = Boolean(selectedDate?.hasSame(day, "day"))
          const ariaLabel = `${day.toFormat("EEEE, LLLL d, yyyy")}${
            isPast ? `, ${i18n.t("app.public.venue.sidebar.unavailable")}` : ""
          }`

          let stateClasses: string
          if (isSelected) {
            stateClasses = "border-[#2176c1] bg-[#2596fa] font-medium text-white"
          } else if (isPast) {
            stateClasses = "cursor-not-allowed border-[#e9e9e9] bg-white text-[#374151] opacity-30"
          } else if (isToday) {
            stateClasses = "border-[#e9e9e9] bg-[#f8f8f8] font-semibold text-[#374151]"
          } else {
            stateClasses = "border-[#e9e9e9] bg-white text-[#374151] hover:border-[#d7dce2]"
          }

          return (
            <button
              key={isoDate}
              type="button"
              disabled={isPast}
              onClick={() => onChange(day.startOf("day"))}
              aria-label={ariaLabel}
              aria-pressed={isSelected}
              data-selected={isSelected}
              data-testid={`public-venue-mobile-booking-day-${isoDate}`}
              className={`flex h-[34px] items-center justify-center rounded-[3px] border-[0.5px] text-[12px] leading-normal transition-colors ${stateClasses}`}
            >
              <span className={isClosed && !isSelected ? "line-through" : undefined}>{day.day}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default MobileCalendar
